import { FeatureDefinition, GrowthBook } from "@growthbook/growthbook";
import { captureException } from "@sentry/nextjs";
import { getToken } from "@website/hooks/auth-manager";
import { setTracking } from "@website/store/tracking";
import { productType } from "@website/ticket-ui/common/constants";
import { USER_TRACKING_EVENTS } from "@website/tracking/tracking.types";
import {
  ProductPropertyMap,
  addEventToTracking,
  ensureTrackingAid
} from "@website/tracking/user";
import { isChannel } from "@website/utils/channel/helpers";
import jwtDecode from "jwt-decode";
import { getChannel } from "../../channel/get-channel";

declare global {
  interface Window {
    __GB_FEATURES__?: Record<string, FeatureDefinition>;
  }
}

let growthbook: GrowthBook | null = null;

const getUserId = () => {
  const token = getToken().stToken;

  if (!token) return undefined;

  try {
    return jwtDecode<{ sub?: string }>(token)?.sub;
  } catch {
    return undefined;
  }
};

export const ExperimentSDK = {
  getGrowthBook(): GrowthBook | null {
    if (typeof window === "undefined") {
      return null;
    }
    if (growthbook) return growthbook;

    const aid = ensureTrackingAid();

    //TODO:  userId and channel is necessary in profile?
    const userId = getUserId();
    const channel = getChannel();

    const features: Record<string, FeatureDefinition> =
      typeof window !== "undefined" && window.__GB_FEATURES__
        ? window.__GB_FEATURES__
        : {};

    growthbook = new GrowthBook({
      features,
      enabled: isChannel(["website", "jek"]),
      enableDevMode: process.env.NODE_ENV !== "production",
      subscribeToChanges: false,
      attributes: {
        id: aid || undefined,
        userId,
        channel
      },
      trackingCallback: (experiment, result) => {
        setTracking((previous) => {
          const { key = "" } = experiment;
          const { value } = result;
          const currentValue = previous.experiment?.[key];
          if (currentValue === value) return previous;
          return {
            ...previous,
            experiment: {
              ...previous.experiment,
              [key]: value
            }
          };
        });
        addEventToTracking(USER_TRACKING_EVENTS.EXPERIMENT_VIEWD, {
          product: productType as keyof ProductPropertyMap,
          experiment_name: experiment.key,
          experiment_id: experiment.key,
          variation_name: result.name,
          variation_id: result.key
        });
      }
    });

    try {
      growthbook.initSync({ payload: { features } });
    } catch {
      captureException(`Failed to create growthbook instance`, {
        tags: {
          errorType: "globalError",
          appName: process.env.NEXT_PUBLIC_APP_NAME,
          appVersion: process.env.NEXT_PUBLIC_IMAGE_VERSION
        }
      });
      return null;
    }

    return growthbook;
  },

  isOn(featureName: string) {
    return this.getGrowthBook()?.isOn(featureName) ?? false;
  },

  evalFeature(featureName: string) {
    return this.getGrowthBook()?.evalFeature(featureName) ?? { value: null };
  },

  destroy() {
    if (growthbook) {
      growthbook.destroy();
      growthbook = null;
    }
  }
};
